import { EntryMethod } from '../../common/enumerations/entry-method';
import { TenderType } from '../../common/enumerations/tender-type';
import { getValueAsNumber } from '../../common/helpers/number-helper';
import { ITenderAdd, PaymentUsage, RefundUsage } from '../../common/interfaces/dtos/IPreTenderAdd';
import { IReceiptArgs } from '../../common/interfaces/dtos/IReceiptArgs';
import { ITender } from '../../common/interfaces/dtos/ITender';
import { IWicCheckDetails } from '../../common/interfaces/dtos/IWicCheckDetails';
import {
  CardDetails,
  EpsData,
  EwicEpsPrescriptionData,
  PaymentMessage,
} from 'src/devices/payment/dtos/eps-data';
import { buildReceipts } from './build-receipt-lines';

// TenderAmountViewModelBase.cs:308
export interface BuildLineTenderProps {
  tender: ITender;
  amount: number;
  lineItemEntryMethod: EntryMethod;
  amountEntryMethod?: EntryMethod;
  payment?: PaymentMessage;
  receiptPreferences?: IReceiptArgs;
  typeCode?: RefundUsage;
  ewicEpsPrescriptionData?: EwicEpsPrescriptionData;
  lineId?: number;
  usage?: PaymentUsage | RefundUsage;
}

export const buildTenderAddDTO = (
  {
    tender,
    amount,
    amountEntryMethod = EntryMethod.Automatic,
    payment,
    receiptPreferences,
    typeCode,
    ewicEpsPrescriptionData,
    lineItemEntryMethod,
    lineId,
    usage,
  }: BuildLineTenderProps,
  epsData?: EpsData,
): ITenderAdd => {
  const returnTenderAdd: ITenderAdd = {
    // TODO: BeginDateTime:
    tender: {
      tenderType: tender?.type,
      currency: tender?.currency?.symbol,
      entryMethod: lineItemEntryMethod,
      cancel: false,
      processedByEps: tender.isProcessedByEps,
      tenderId: tender.id,
      expirationDate: tender.expirationDate,
      usage,
    },
    lineId: lineId,
    epsData: epsData,
    amount: {
      amount: amount,
      amountEntryMethod: amountEntryMethod,
    },
    receiptPreferences: {
      printReceipt: receiptPreferences?.printReceipt,
      emailAddress: receiptPreferences?.emailAddress,
      phoneNumber: receiptPreferences?.mobilePhoneNumber,
      deliveryMethod: receiptPreferences?.receiptDeliveryPreference,
    },
    typeCode: typeCode,
    ...(payment && buildEpsData(payment)),
  };
  if (payment?.amounts) {
    const cashback = getValueAsNumber(payment?.amounts?.cashbackAmountApproved);
    const amountApproved = getValueAsNumber(payment.amounts.amountApproved);
    const amountRequested = getValueAsNumber(payment.amounts.amountDue);
    if (returnTenderAdd.amount) {
      returnTenderAdd.amount.cashBack = cashback;
      returnTenderAdd.amount.amount = amountApproved;
      returnTenderAdd.amount.preTenderAmount = amount;
      returnTenderAdd.amount.requestedAmount = amountRequested;
    }
  }
  if (ewicEpsPrescriptionData) {
    returnTenderAdd.ewicEpsPrescriptionData = ewicEpsPrescriptionData;
  }
  return returnTenderAdd;
};

const buildEpsData = (payment: PaymentMessage): ITenderAdd => {
  const lineTender: ITenderAdd['epsData'] = {
    authorization: {
      epsName: 'CCL',
      authorizationMethod: 'Online',
      isSoftDeclined: false,
      signatureRequiredFlag: payment.paymentDetails?.needsSignature,
    },
    isSmartCardDeclinedByTerminal: false,
    customerSignatureRejectedByCashier: false,
  };

  if (payment.cardDetails) {
    const cardDetails: CardDetails = payment.cardDetails[0];
    lineTender.card = {
      maskedAccountNumber: `${cardDetails.firstSix}${'*'.repeat(
        (cardDetails.cardLength ?? 16) - 10,
      )}${cardDetails.lastFour}`,
      cardType: cardDetails.cardType,
      entryMethod: cardDetails.entryMethod,
      cardCode: cardDetails.cardCode,
      extendedData: cardDetails.extendedData,
    };
    if (lineTender.authorization) {
      lineTender.authorization.merchantNumber = cardDetails.merchantNumber;
    }
  }

  if (payment.receipts) {
    lineTender.card = lineTender.card ?? {};
    buildReceipts(lineTender.card, payment.receipts);
  }

  if (payment.status) {
    lineTender.status = payment.status;
  }

  if (payment.paymentDetails) {
    const paymentDetails = payment.paymentDetails;
    // lineTender.ExtendedPaymentParameters = [];
    if (lineTender.authorization) {
      if (paymentDetails.authCode) {
        lineTender.authorization.authorizationCode = paymentDetails.authCode;
      }
      if (paymentDetails.responseCode) {
        lineTender.authorization.responseCode = paymentDetails.responseCode;
      }
      if (paymentDetails.paymentTranID) {
        lineTender.authorization.referenceNumber = paymentDetails.paymentTranID;
      }
    }
    if (paymentDetails.sessionTranID) {
      lineTender.sessionTranID = paymentDetails.sessionTranID;
    }
    if (paymentDetails.providerData) {
      lineTender.providerData = paymentDetails.providerData;
    }
  }
  // if (epsData.deviceData) {
  //   const deviceData = epsData.deviceData;
  //   lineTender.TerminalId = deviceData.serialNumber;
  // }
  if (lineTender.authorization) {
    lineTender.authorization.epsName = 'CCL'; //TODO: need to figure it out.
  }
  return {
    epsData: lineTender,
  };
};

// TenderAmountViewModelBase.cs:341
// BM - TODO: This method calls a model inspector in the legacy POS code. Need to determine how to port over code
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tryGetWicCheckDetails = (selectedTender: ITender): IWicCheckDetails | undefined => {
  if (selectedTender.type !== TenderType.WicCheck && selectedTender.type !== TenderType.WicCvv) {
    return undefined;
  }

  /**
   * let wicCheckDetailsModelInspector = ModelInspectorProvider.Get<IWiccheckDetailsModelInspector>();
   *
   * if (wicCheckDetailsModelInspector === null) {
   *  return null
   * }
   */

  return undefined;
};

// TenderAmountViewModelBase.cs:330
// const tryGetVoucherPaymentTermReferenceAndUpdateAmount = (
//   selectedTender: ITender,
// ): number | undefined => {
//   const selectedVoucherDetails = selectedTender.selectedVoucherDetails;

//   if (selectedVoucherDetails === undefined) {
//     return undefined;
//   }

//   return selectedVoucherDetails.sequenceNumber;
// };
